/**
 * Global activity logger — records user actions to the activity_logs table.
 * Batches writes to avoid spamming the DB on rapid actions.
 */
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";

export type ActivityCategory =
  | "ai_chat"        // LLM interactions (world builder, agent chat, adventure)
  | "tool_use"       // AI tool calls (create_entity, update_world, etc.)
  | "image_gen"      // Image generation / brief planning
  | "navigation"     // Page navigation
  | "entity"         // Entity CRUD
  | "world"          // World CRUD
  | "adventure"      // Adventure actions
  | "auth"           // Login / logout
  | "settings"       // Settings changes
  | "document"       // Document upload / processing
  | "tts"            // Text-to-speech
  | "general";       // Catch-all

export interface ActivityEntry {
  action: string;
  category: ActivityCategory;
  details?: Record<string, any>;
  source?: string; // e.g. component name or edge function
}

// In-memory queue for batching
const queue: (ActivityEntry & { user_id: string; created_at: string })[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cachedUserId: string | null = null;

const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 3000;

async function getUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId;
  try {
    const user = await getSessionUser();
    cachedUserId = user?.id || null;
    return cachedUserId;
  } catch {
    return null;
  }
}

// Listen for auth changes to update cached user
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id || null;
});

async function flushQueue() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, BATCH_SIZE);
  try {
    await supabase.from("activity_logs" as any).insert(
      batch.map((e) => ({
        user_id: e.user_id,
        action: e.action,
        category: e.category,
        details: e.details || {},
        source: e.source || "",
        created_at: e.created_at,
      }))
    );
  } catch {
    // Silent fail — activity logging should never break the app
  }
  // If there's more in the queue, schedule another flush
  if (queue.length > 0) {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Sanitize details for confidential worlds — strip content-bearing fields.
 */
function sanitizeForConfidential(entry: ActivityEntry): ActivityEntry {
  if (!entry.details?.is_confidential) return entry;
  const { content, description, name, ...safeDetails } = entry.details;
  return { ...entry, details: safeDetails };
}

/**
 * Log an activity. Non-blocking — queues and batches writes.
 */
export function logActivity(entry: ActivityEntry) {
  getUserId().then((userId) => {
    if (!userId) return;
    const sanitized = sanitizeForConfidential(entry);
    queue.push({
      ...sanitized,
      user_id: userId,
      created_at: new Date().toISOString(),
    });
    if (queue.length >= BATCH_SIZE) {
      flushQueue();
    } else {
      scheduleFlush();
    }
  });
}

/** Force flush any pending logs (call on page unload). */
export function flushActivityLogs() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushQueue();
}
